// Lib
import React, { MutableRefObject, ReactElement, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { EditorContent } from '@tiptap/react';
import useCommentTiptapEditorExtensions from './useCommentTiptapEditorExtensions';
import { useSuggestionsManager } from '../../../components/tiptapEditor/useSuggestionsManager';
import useClientElementTiptapEditor from '../../../components/tiptapEditor/useClientElementTiptapEditor';
import { TiptapContent } from '../../../../common/tiptap/tiptapTypes';
import CommentTiptapPostButton from './CommentTiptapPostButton';
import useEditorActivity from './useEditorActivity';
import { SearchHighlightObserver } from '../../../../common/tiptap/extensions/SearchHighlighter';
import useTiptapInnerRef from '../../../../common/tiptap/hooks/useTiptapInnerRef';
import usePoiUpdateHandler from '../../../components/pointsOfInterest/usePoiUpdateHandler';
import { Editor as TiptapEditor } from '@tiptap/core';
import { SaveTiptapEditorContentFunction } from '../../../../common/tiptap/extensions/externalStoreSync/externalStoreSynchroniserTypes';

export type CommentTiptapEditorProps = {
    commentId?: string;
    parentThreadElementId?: string;
    tiptapContent: TiptapContent | null;
    placeholder: string;

    isUpdating: boolean;
    isEditing: boolean;
    isEditable: boolean;
    isSingleSelected: boolean;

    spellCheck: boolean;
    hidePostButton?: boolean;

    editorId: string;
    editorKey: string;
    currentEditorKey: string;
    currentEditorId: string;

    saveContent: SaveTiptapEditorContentFunction;
    onSubmit: (textContent: TiptapContent) => void;
    onEmptyBackspace: () => void;
    startEditing: () => void;
    onActivity?: () => void;

    editorInstanceRef?: MutableRefObject<TiptapEditor | null>;

    // Editing lifecycle callbacks - named after the events that trigger them
    onBlur?: () => void; // Called when the editor loses focus
};

export const CommentTiptapEditor = (props: CommentTiptapEditorProps): ReactElement => {
    const {
        parentThreadElementId,
        tiptapContent,
        placeholder,
        isUpdating,
        isEditing,
        isEditable,
        isSingleSelected,
        spellCheck,
        hidePostButton,
        editorId,
        editorKey,
        currentEditorId,
        saveContent,
        onSubmit,
        onEmptyBackspace,
        startEditing,
        onActivity,
        editorInstanceRef,
    } = props;

    const dispatch = useDispatch();

    const { innerRef, contentInnerRef } = useTiptapInnerRef();
    const onHighlightsChanged = usePoiUpdateHandler({ contentInnerRef, elementId: parentThreadElementId });

    const [suggestionsManager, mentionsPopup] = useSuggestionsManager();

    const extensions = useCommentTiptapEditorExtensions({
        placeholder,
        onEmptyBackspace,
        saveContent,
        suggestionsManager,
        isEditable,
        dispatch,
        onSubmit,
        onHighlightsChanged,
    });

    const { editor, onMouseDown, onClick } = useClientElementTiptapEditor({
        persistedContent: tiptapContent,
        extensions,

        editorId,
        editorKey,
        currentEditorId,

        isEditable,
        isEditing,
        isSingleSelected,

        startEditing,
        saveContent,
    });

    // Used by the CommentSaveButton to get the current editor state
    if (editorInstanceRef) {
        editorInstanceRef.current = editor;
    }

    /**
     * Comment threads aren't editable until they're selected, so the default logic within
     * the onClick handler of useClientElementTiptapEditor isn't sufficient here.
     *
     * We override it when not editable, to allow event propagation higher up the tree &
     * allow the comment thread to determine whether to trigger a selection or prevent
     * default (e.g. on a read-only board).
     */
    const commentEditorClickHandler = useCallback(
        (event) => {
            if (!isEditable) return;
            onClick(event);
        },
        [onClick, isEditable],
    );

    useEditorActivity(editor, onActivity);

    return (
        <div className="CommentEditor" onMouseDown={onMouseDown} onClick={commentEditorClickHandler}>
            {mentionsPopup}
            <SearchHighlightObserver editor={editor} />
            <EditorContent
                innerRef={innerRef}
                className="CommentTiptapEditor"
                editor={editor}
                dir="auto"
                spellCheck={spellCheck}
            />
            {editor && !hidePostButton && isUpdating && <CommentTiptapPostButton onSubmit={onSubmit} editor={editor} />}
        </div>
    );
};
